import BackendSyncWebSocketService from "./backendSyncWebsocketService";
import BackendSyncSSEService from "./backendSyncSSEService";
import store from "@/store";
import { showOffcanvas } from "@/utils";
import { useNotificationStore } from "@/stores/notificationStore";
import { applyMenuProductAvailabilityFromWebSocket, applyProductVirtualStockFromWebSocket } from "./copilotProductStockSync";
import { buildClientId, buildMercureSubscribePayload, buildSseTopics, SYNC_PROFILES } from "./backendSyncProfiles";
import { resolveSyncTransport } from "./resolveSyncTransport";
import { mercureRepository } from "./repository/mercureRepository";

class BackendSyncService {
	static ACTION_ENABLE_MENU_BUTTON = 0;
	static ACTION_UPDATE_RESERVATION_ORDERS = 1;
	static ACTION_SEND_NOTIFICATION_USER_VALIDATE = 2;
	static ACTION_SEND_NOTIFICATION_RESERVATION_VALIDATE = 3;
	static ACTION_UPDATE_RESERVATION_IN_COPILOT = 4;
	static ACTION_UPDATE_NOTIFICATIONS_IN_COPILOT = 5;
	static ACTION_DISABLE_MENU = 6;
	static ACTION_SEND_NOTIFICATION = 7;
	static ACTION_RELOAD = 8;
	static ACTION_ADD_SERVICE_EVENT = 9;
	static ACTION_UPDATE_SERVICE_EVENT = 10;
	static ACTION_UPDATE_A_SINGLE_PRODUCT_ORDER = 11;
	static ACTION_ADD_NEW_RESERVATION_IN_COPILOT = 12;
	static ACTION_UPDATE_MENUS = 13;
	static ACTION_UPDATE_RESERVATION_IN_MENU = 14;
	static ACTION_UPDATE_PRODUCT_AVAILABILITY = 15;
	static ACTION_UPDATE_PRODUCT_TOP_CHOICE = 16;
	static ACTION_UPDATE_MENU_PRODUCT = 17;
	static ACTION_MERGED_RESERVATION = 18;
	static ACTION_REASSIGN_PRODUCT_ORDERS_RESERVATION = 19;
	static ACTION_UPDATE_CATALOG = 20;
	static ACTION_UPDATE_BATCH_PRODUCT_ORDERS = 21;
	static ACTION_EVENT_CHECKIN_UPDATE = 22;
	static ACTION_EVENT_WELCOME_GUEST = 23;
	static ACTION_VOICE_MESSAGE = 24;
	static ACTION_VOICE_TRANSCRIPT = 25;
	static ACTION_FORCE_STAFF_LOGOUT = 26;
	static ACTION_UPDATE_PRODUCT_VIRTUAL_STOCK = 27;
	static ACTION_RELOAD_WEHOST = 28;

	constructor() {
		this.syncService = null;
		this.eventHandler = null;
		this.connectionActive = false;
		this.componentReference = null;
		this.clientId = null;
		this.activeProfile = null;
		this.isInitialConnection = true;
	}

	setComponentReference(component) {
		this.componentReference = component;
	}

	setClientId(clientId) {
		this.clientId = clientId;
	}

	/**
	 * Connect using a profile-based configuration.
	 * @param {{
	 *   profile: 'copilot'|'user'|'wehost',
	 *   transport?: 'sse'|'websocket',
	 *   context: { venueId?: number|string, userId?: string, reservationId?: number|string, deviceToken?: string },
	 *   componentRef?: object|null,
	 * }} config
	 */
	async connect(config) {
		this.teardownTransport();

		this.activeProfile = config.profile;
		this.componentReference = config.componentRef ?? null;
		this.clientId = buildClientId(config.profile, config.context);
		this.isInitialConnection = true;

		const transport = config.transport ?? resolveSyncTransport();

		if (transport === "sse") {
			await mercureRepository.subscribe(buildMercureSubscribePayload(config.profile, config.context));
			this.syncService = new BackendSyncSSEService({
				topics: buildSseTopics(config.profile, config.context),
			});
		} else {
			this.syncService = new BackendSyncWebSocketService(this.clientId);
		}

		this.eventHandler = this.handleIncomingEvent.bind(this);
		this.syncService.addMessageListener(this.eventHandler);
		this.connectionActive = true;
		store.dispatch("backendSync/setConnecting");
		this.syncService.connect();
	}

	/** @deprecated Use connect({ profile, context, ... }) */
	initialize(syncMethod = "websocket") {
		if (this.syncService) {
			console.warn("[BackendSyncService] Already connected. Call disconnect() first or use connect().");
			return;
		}

		switch (syncMethod) {
			case "websocket":
				this.syncService = new BackendSyncWebSocketService(this.clientId);
				break;
			case "sse":
				this.syncService = new BackendSyncSSEService({
					topics: buildSseTopics(SYNC_PROFILES.COPILOT, {
						venueId: String(this.clientId ?? "").replace(/^COPILOT_/, ""),
					}),
				});
				break;
			default:
				throw new Error(`Unsupported sync method: ${syncMethod}`);
		}

		this.eventHandler = this.handleIncomingEvent.bind(this);
		this.syncService.addMessageListener(this.eventHandler);
	}

	/** @deprecated Use connect() */
	connectLegacy() {
		if (!this.syncService) {
			console.error("Sync service not initialized. Call initialize() first.");
			return;
		}
		if (this.connectionActive) {
			console.warn("Already connected. Skipping connect()");
			return;
		}
		this.connectionActive = true;
		store.dispatch("backendSync/setConnecting");
		this.syncService.connect();
	}

	setComponentRef(componentRef) {
		this.componentReference = componentRef;
	}

	disconnect() {
		this.teardownTransport();
	}

	teardownTransport() {
		if (this.syncService && this.eventHandler) {
			this.syncService.removeMessageListener(this.eventHandler);
		}
		if (this.syncService) {
			this.syncService.disconnect();
			this.syncService = null;
		}
		this.eventHandler = null;
		this.connectionActive = false;
		this.activeProfile = null;
		store.dispatch("backendSync/setDisconnected");
	}

	/**
	 * Manually trigger a data refresh to ensure sync with backend
	 * This can be called when there's suspicion that data might be out of sync
	 */
	async refreshData() {
		if (this.componentReference && this.componentReference.updateReservations && this.componentReference.updateNotifications) {
			console.log("[BackendSyncService] Manually refreshing data to ensure sync");
			try {
				await this.componentReference.updateReservations();
				await this.componentReference.updateNotifications();
				console.log("[BackendSyncService] Manual data refresh completed");
			} catch (error) {
				console.error("[BackendSyncService] Error during manual data refresh:", error);
			}
		}
	}

	sendMessage(message) {
		if (this.syncService) {
			this.syncService.sendMessage(message);
		}
	}

	async handleIncomingEvent(type, data) {
		// type could be: 'connected', 'disconnected', 'error', or 'message' (with data).
		// data in the 'message' case might have something like { action: ..., ... }

		switch (type) {
			case "connecting":
				console.log("[BackendSyncService] WebSocket attempting to connect/reconnect");
				store.dispatch("backendSync/setConnecting");
				break;

			case "connected":
				console.log("[BackendSyncService] WebSocket connected");
				store.dispatch("backendSync/setConnected");

				// Check if this is a reconnection (not the initial connection)
				if (!this.isInitialConnection && this.componentReference && this.componentReference.updateReservations && this.componentReference.updateNotifications) {
					console.log("[BackendSyncService] Reconnection detected - refreshing data to ensure sync");

					// Refresh reservations and notifications to ensure data is in sync
					try {
						await this.componentReference.updateReservations();
						await this.componentReference.updateNotifications();
						console.log("[BackendSyncService] Data refresh completed after reconnection");
					} catch (error) {
						console.error("[BackendSyncService] Error refreshing data after reconnection:", error);
					}
				}

				// Mark that we've had at least one connection
				this.isInitialConnection = false;
				break;

			case "disconnected":
				console.log("[BackendSyncService] WebSocket disconnected");
				store.dispatch("backendSync/setDisconnected");
				break;

			case "error":
				console.error("[BackendSyncService] WebSocket error:", data);
				store.dispatch("backendSync/setError", data);
				break;

			case "message":
				if (!data || !data.action) {
					console.warn("[BackendSyncService] Received message without an action", data);
					return;
				}
				this.handleIncomingMessageAction(data.action, data);
				break;

			default:
				console.warn("[BackendSyncService] Unknown event type:", type, "with data:", data);
				break;
		}
	}

	/**
	 * This method replaces the old "switch(action)" inside the WebSocketClient.
	 * We unify that logic here so that in the future we can share it
	 * across multiple sync methods (WebSocket, SSE, Mercure, etc.).
	 */
	async handleIncomingMessageAction(action, data) {
		if (this.activeProfile === SYNC_PROFILES.WEHOST) {
			return this.handleWehostIncomingMessageAction(action, data);
		}

		console.log("[BackendSyncService] Handling message action with data:", action, data);
		// You might rename these constants in your backend or pass them as strings
		// but let's assume you keep your ACTION_* enumerations.

		switch (action) {
			case BackendSyncService.ACTION_ENABLE_MENU_BUTTON: // ACTION_ENABLE_MENU_BUTTON
				// store.commit(...) or store.dispatch(...) as needed
				// ...
				break;

			case BackendSyncService.ACTION_UPDATE_RESERVATION_ORDERS: // ACTION_UPDATE_RESERVATION_ORDERS
				await store.dispatch("copilot/updateOrdersAndNotifications");
				return this.componentReference.updateReservations();

			case BackendSyncService.ACTION_SEND_NOTIFICATION_RESERVATION_VALIDATE:
				console.log("reserva validada");
				window.location.href = data.data;
				window.parent.postMessage({ event: "redirectPassFromIframe", data: { url: data.data } }, "*");
				break;

			case BackendSyncService.ACTION_UPDATE_RESERVATION_IN_COPILOT: {
				const updatedReservation = data.data.reservation;
				const type = updatedReservation.type?.toLowerCase();

				if (type === 'delivery') {
					store.commit("copilot/updateDeliveryReservation", { updatedReservation });
				} else if (type === 'takeaway') {
					store.commit("copilot/updateTakeawayReservation", { updatedReservation });
				} else {
					store.commit("copilot/updateReservation", { updatedReservation });
				}

				// Update current reservation in reservation store if it matches
				const currentReservation = store.getters['reservation/reservation'];
				const updatedId = updatedReservation.realId || updatedReservation.id;
				const currentId = currentReservation?.realId || currentReservation?.id;
				if (currentReservation && currentId === updatedId) {
					store.commit("reservation/setReservation", { ...updatedReservation });
				} else if (!currentReservation) {
					store.commit("reservation/setReservation", updatedReservation);
				}

				document.dispatchEvent(new Event("buttonTextOrderUpdated"));
				if (data.data.updateNotifications) {
					await store.dispatch("copilot/updateOrdersAndNotifications");
				}
				break;
			}

			case BackendSyncService.ACTION_UPDATE_NOTIFICATIONS_IN_COPILOT:
				await store.dispatch("copilot/updateOrdersAndNotifications");
				break;

			case BackendSyncService.ACTION_RELOAD: {
				const reloadTimeout = 1 * 1000;
				console.log("Recibido comando de recarga con datos", data);
				const reason = data.data.reason;
				const roles = data.data.roles;
				const selectedRole = store.getters["copilot/getSelectedRole"];

				console.log("Roles a los que afecta: " + roles.join(", ") + " | Motivo: " + reason);

				if (!roles || roles.length === 0) {
					setTimeout(() => location.reload(), reloadTimeout);
					break;
				}

				const hasAllRole = roles.some((role) => role.toLowerCase() === "all");
				const matchesSelectedRole = roles.some((role) => role.toLowerCase() === selectedRole.name.toLowerCase());

				if (hasAllRole || matchesSelectedRole) {
					console.log("Reloading the page due to matching role.");
					setTimeout(() => location.reload(), reloadTimeout);
				} else {
					console.log("No matching role found. No action taken.");
				}

				break;
			}
			// e.g.
			case BackendSyncService.ACTION_SEND_NOTIFICATION: // ACTION_SEND_NOTIFICATION
				try {
					useNotificationStore().upsertPendingAccountSeatedFromPush(data.data);
				} catch (error) {
					console.error("[BackendSyncService] Error upserting pending account seated from push:", error);
				}
				store.commit("copilot/setNewNotification", { newNotification: data.data });
				break;

			case BackendSyncService.ACTION_DISABLE_MENU:
				console.log(data.data, "desde la acción la reserva actual", this.componentReference.reservationInfo);
				this.componentReference.reservationInfo = {
					...this.componentReference.reservationInfo,
					menuAccessEnabled: data.data.menuAccessEnabled,
				};

				console.log("La reserva después de cambiarla", this.componentReference.reservationInfo);
				break;
			case BackendSyncService.ACTION_ADD_SERVICE_EVENT: {
				const serviceEvent = data.data.serviceEvent;
				const productOrdersIdsToUpdate = data.data.productOrderIdsToUpdate;

				console.log("El servicio event recibido por WS: ", serviceEvent);
				console.log("El productOrdersIdsToUpdate event recibido por WS: ", productOrdersIdsToUpdate);

				store.commit("copilot/updateProductOrdersStatus", { productOrdersIdsToUpdate, status: 3 });
				store.commit("copilot/addServiceEvent", { serviceEvent });
				break;
			}

			case BackendSyncService.ACTION_UPDATE_SERVICE_EVENT: {
				const serviceEvent = data.data.serviceEvent ?? null;
				const serviceEvents = data.data.serviceEventsToUpdate ?? [];
				const productOrdersIdsToUpdate = data.data.productOrderIdsToUpdate;
				const nextStatusProductOrder = data.data.nextStatusProductOrder;

				store.commit("copilot/updateProductOrdersStatus", { productOrdersIdsToUpdate, status: nextStatusProductOrder });
				if (serviceEvent) {
					store.commit("copilot/updateServiceEvent", { serviceEvent });
				}

				serviceEvents.forEach((serviceEventToUpdate) => store.commit("copilot/updateServiceEvent", { serviceEvent: serviceEventToUpdate }));

				break;
			}

			case BackendSyncService.ACTION_UPDATE_A_SINGLE_PRODUCT_ORDER: {
				const productOrderIdToUpdate = data.data.productOrderIdToUpdate;
				const status = data.data.status;

				console.log("El productOrderIdToUpdate event recibido por WS: ", productOrderIdToUpdate);

				store.commit("copilot/updateSingleProductOrder", { productOrderIdToUpdate: productOrderIdToUpdate, status: status });
				break;
			}

			case BackendSyncService.ACTION_UPDATE_BATCH_PRODUCT_ORDERS: {
				const updates = data.data.updates;
				if (Array.isArray(updates)) {
					store.commit("copilot/updateBatchProductOrders", { updates });
				}
				break;
			}

			case BackendSyncService.ACTION_ADD_NEW_RESERVATION_IN_COPILOT: {
				const reservation = data.data;
				const type = reservation.type?.toLowerCase();

				if (type === 'delivery') {
					store.commit("copilot/addDeliveryReservationToReservations", { reservation });
				} else if (type === 'takeaway') {
					store.commit("copilot/addTakeawayReservationToReservations", { reservation });
				} else {
					store.commit("copilot/addReservationToReservations", { reservation });
				}
				break;
			}

			case BackendSyncService.ACTION_UPDATE_MENUS:
				await store.dispatch("menu/loadMenus");
				break;
			case BackendSyncService.ACTION_UPDATE_CATALOG: {
				const reason = data.data?.reason ?? "reload_catalog";
				console.log("[BackendSyncService] Reload catalog:", reason);
				await store.dispatch("menu/loadMenus");
				break;
			}

			case BackendSyncService.ACTION_UPDATE_RESERVATION_IN_MENU: {
				const updatedReservation = data.data.reservation;
				const showModalReview = data.data.showModalReview;
				const userId = this.componentReference.user.id;
				const hasUserRatedReservation = updatedReservation.userReviews.map((review) => review.userId).includes(userId);

				this.componentReference.reservationInfo = updatedReservation;

				console.log("updatedReservation", updatedReservation);
				console.log("showModalReview", showModalReview);
				if (showModalReview && !hasUserRatedReservation) {
					showOffcanvas("menu-modal-review");
				}
				break;
			}

			case BackendSyncService.ACTION_UPDATE_PRODUCT_AVAILABILITY: {
				applyMenuProductAvailabilityFromWebSocket({
					productSku: data.data.productSku,
					menuCategoryId: data.data.menuCategoryId,
					newAvailability: data.data.availability,
				});
				break;
			}

			case BackendSyncService.ACTION_UPDATE_PRODUCT_TOP_CHOICE: {
				const sku = data.data.productSku;
				const topChoice = data.data.topChoice;

				store.commit("menu/updateProductTopChoice", { sku, topChoice });
				break;
			}

			case BackendSyncService.ACTION_UPDATE_MENU_PRODUCT: {
				const product = data.data.product;
				store.commit("menu/updateMenuProduct", { product });
				break;
			}

			case BackendSyncService.ACTION_MERGED_RESERVATION: {
				const reservationToUpdate = data.data.reservationToUpdate;
				const mergedReservationId = data.data.mergedReservationId;
				store.commit("copilot/updateReservationsAfterMerging", { reservationToUpdate, mergedReservationId });
				break;
			}

			case BackendSyncService.ACTION_REASSIGN_PRODUCT_ORDERS_RESERVATION: {
				const reassignedProductOrderIds = data.data.reassignedProductOrderIds;
				const reservationUpdated = data.data.reservationUpdated;
				store.commit("copilot/reassignProductOrdersReservation", { reassignedProductOrderIds, reservationUpdated });
				break;
			}

			case BackendSyncService.ACTION_EVENT_CHECKIN_UPDATE: {
				// Update the event registration list in the Pinia event store
				try {
					const { useEventStore } = await import("@/stores/eventStore");
					const eventStore = useEventStore();
					eventStore.updateRegistrationFromWS(data.data);
				} catch (e) {
					console.warn("[BackendSyncService] Event store not available for check-in update", e);
				}
				break;
			}

			case BackendSyncService.ACTION_VOICE_MESSAGE: {
				try {
					const { useVoiceIntercomStore } = await import("@/stores/voiceIntercomStore");
					const voiceStore = useVoiceIntercomStore();
					voiceStore.handleIncomingVoiceMessage(data.data);
				} catch (e) {
					console.warn("[BackendSyncService] Voice intercom store not available", e);
				}
				break;
			}

			case BackendSyncService.ACTION_VOICE_TRANSCRIPT: {
				try {
					const { useVoiceIntercomStore } = await import("@/stores/voiceIntercomStore");
					const voiceStore = useVoiceIntercomStore();
					voiceStore.handleTranscriptUpdate(data.data);
				} catch (e) {
					console.warn("[BackendSyncService] Voice intercom store not available for transcript", e);
				}
				break;
			}

		case BackendSyncService.ACTION_FORCE_STAFF_LOGOUT: {
			// The backend detected that this device's session is invalid
			// (e.g. the PIN was changed server-side after the last staff list download).
			// Log out the current staff member and let the PIN overlay reappear.
			const reason = data.data?.reason || 'session_invalidated';
			console.warn('[BackendSyncService] Force staff logout received. Reason:', reason);
			store.dispatch('copilot/forceLogoutStaff', { reason });
			break;
		}

		case BackendSyncService.ACTION_UPDATE_PRODUCT_VIRTUAL_STOCK: {
			applyProductVirtualStockFromWebSocket(data.data);
			break;
		}

		default:
				console.warn("[BackendSyncService] Unhandled action:", action, "with data:", data);
				break;
		}
	}

	handleWehostIncomingMessageAction(action, data) {
		console.log("[BackendSyncService] Handling wehost action:", action, data);

		switch (action) {
			case BackendSyncService.ACTION_RELOAD_WEHOST: {
				const reloadTimeout = 1 * 1000;
				const reason = data.data?.reason ?? "reload";
				console.log("[BackendSyncService] WeHost reload requested:", reason);
				setTimeout(() => location.reload(), reloadTimeout);
				break;
			}

			default:
				console.warn("[BackendSyncService] Unhandled wehost action:", action, "with data:", data);
				break;
		}
	}
}

// Export as a singleton (like your original new BackendSyncService())
export default new BackendSyncService();
